import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { IntervalExplorer } from "@/components/mt/IntervalExplorer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory8() {
  const { lesson, course, accent } = findLesson("music-theory", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT8-Intervals-Naming-Animated.mp4"
        caption="Count twice — letter names give the NUMBER, semitones give the QUALITY"
        hint="≈ 26 sec · watch first"
      />
      <Chapter number="08" eyebrow="Music Theory · Chapter Eight" title="What an interval really is">
        <p>
          The textbook will tell you that an interval is the <em>distance</em> between two
          notes. That's a tidy definition — and it misses the entire point. Distance is a
          geometric idea; music is an emotional one. A better way to think about an interval is
          this:
        </p>
        <MTQuote>
          What I think it is, is the vibe or the mood or the emotion created when two
          musical notes collide. I don't see how distance is a thing.
        </MTQuote>
        <p>
          A single note is just a sound. A car horn is a single note. Music begins when two
          notes meet — melodically, one after the other, or harmonically, both at once — and
          their collision creates the ear's first taste of mood. Stable, restless, hopeful,
          tense, mysterious. Intervals are the atomic emotions of music.
        </p>
      </Chapter>

      <Chapter eyebrow="Four families" title="Resolution · Tension · Anticipation · Mystery">
        <Prose>
          <p>
            Theory's traditional sorting is a blunt pair: consonance and dissonance. Useful,
            but it lumps very different feelings together. Jason teaches a finer set of four —
            <strong>resolution</strong>, <strong>tension</strong>,
            <strong> anticipation</strong>, and <strong>mystery</strong> — the last one for the
            intervals whose role keeps shifting depending on the harmony beneath them.
          </p>
        </Prose>

        <Glossary>
          <Term name="Resolution" def="At rest. Stable. Sounds 'done'. Where music wants to land." />
          <Term name="Tension" def="Restless. Dissonant. Wants to move somewhere else, urgently." />
          <Term name="Anticipation" def="Bridging. Glides between rest and tension; the connector." />
          <Term name="Mystery" def="Context-dependent. Sometimes home, sometimes restless. The composer's wildcard." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The resolutions" title="Unison · octave · perfect 5th · major 3rd · minor 3rd">
        <Prose>
          <p>
            The resolutions are the intervals that the ear hears as <em>finished</em>. Play one
            and walk away — the listener is satisfied. Their physics is simple:
            small-whole-number frequency ratios (1:1, 2:1, 3:2, 5:4, 6:5). Their emotional
            payload is "we have arrived".
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Unison</strong> — the same note doubled. The simplest possible "yes".</li>
            <li><strong>Octave</strong> — same letter, eight degrees away. Stable, open, complete.</li>
            <li><strong>Perfect 5th</strong> — the boldest stable interval; the spine of every chord.</li>
            <li><strong>Major 3rd</strong> — stable and <em>bright</em>. Optimism made into sound.</li>
            <li><strong>Minor 3rd</strong> — stable but <em>shadowed</em>. Melancholy that has accepted itself.</li>
          </ul>
        </Prose>

        <Prose>
          <p>
            The two thirds — major and minor — are stable in different ways. The major 3rd is
            relief and triumph; the minor 3rd is contemplation and acceptance. Neither one is
            "wrong"; they are just different colours of <em>arrival</em>. Without them you have
            no major or minor scale, no major or minor chord, no tonal music at all.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The anticipations" title="Major 2nd · perfect 4th · minor 7th">
        <Prose>
          <p>
            Anticipations are the bridges. They sound restless, but only mildly — like a car
            slowing down at a yellow light, not yet stopped. They want to move <em>toward</em>
            something stable, and that motion is what gives music its forward feel. Without
            anticipations, music would be a series of disconnected resting points.
          </p>
        </Prose>

        <TheoryBox label="The three anticipations">
          <ul className="space-y-1 text-[14px]">
            <li><strong>Major 2nd</strong> — one whole step up; the gentlest pull (the 'Re' note).</li>
            <li><strong>Perfect 4th</strong> — stable on its own, but yearning when paired with a 3rd or 5th nearby.</li>
            <li><strong>Minor 7th</strong> (the ♭7 — the interval that sits on top of a dominant seventh <em>chord</em>) — slides gracefully down to the 6th or up to the octave.</li>
          </ul>
        </TheoryBox>

        <Prose>
          <p>
            The minor 7th is the most useful of the three. It is the colour note in every
            blues, every dominant chord, every funk groove. Its glide back to the 6 — or up to
            the root — is one of the most musical sounds in Western harmony.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The mysteries" title="Major 6th and minor 6th">
        <Prose>
          <p>
            The 6ths are the strangest characters in the cast. Sometimes they sit perfectly
            still, sounding as resolved as a 3rd or a 5th. Sometimes they ache for movement,
            wanting to step down to the 5th. Their behaviour depends entirely on the chord
            beneath them — change the harmony and the same 6th can flip from "home" to
            "homesick" in an instant.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Major 6th</strong> — warm, pastoral; sometimes a destination, sometimes a stepping-stone.</li>
            <li><strong>Minor 6th</strong> — wistful, almost cinematic; deeply expressive when paired with a minor chord.</li>
          </ul>
          <p>
            The textbooks lump the 6ths in with the anticipations or the resolutions and pick
            one. The truth is they belong to neither completely. They are the wildcards —
            mysteries — and that ambiguity is exactly why composers love them.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The tensions" title="Minor 2nd · major 7th · tritone">
        <Prose>
          <p>
            Tensions are the spice. They sound chaotic in isolation, but in context they are
            the most powerful expressive tools music has. A whole horror genre lives off the
            minor 2nd. A whole dreamy-Lydian genre lives off the tritone. The major 7th, paired
            with a 9th and a 3rd, becomes one of the most beautiful sounds you can play.
          </p>
        </Prose>

        <TheoryBox label="The three tensions">
          <ul className="space-y-1 text-[14px]">
            <li><strong>Minor 2nd</strong> — the half-step; chaos in isolation, exotic in context (think Spanish, Egyptian, ♭9 jazz chords).</li>
            <li><strong>Major 7th</strong> — leads to the octave with magnetic force; the colour note of jazz ballads when surrounded by 3rds.</li>
            <li><strong>Tritone</strong> — three whole-steps; the famous "devil's interval" — but also dreamy in Lydian, also bracing in heavy-metal riffs.</li>
          </ul>
        </TheoryBox>

        <Prose>
          <p>
            None of these intervals are "wrong notes". That description is one of the great
            mistakes of beginner theory. They are tensions — and tensions, used well, are what
            give music its weight, its drama, and its surprise.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Hear all twelve" title="One root, the whole emotional spectrum">
        <Prose>
          <p>
            The explorer below lets you hear every interval from a chosen root. Cycle through
            them and listen for the family each one belongs to. Resolutions feel like rest.
            Anticipations feel like motion. Mysteries feel ambiguous. Tensions feel urgent.
            Once your ear can sort the four families instinctively, you can start using
            intervals like a vocabulary, not a list.
          </p>
        </Prose>

        <IntervalExplorer root="A" />
      </Chapter>

      <Chapter eyebrow="A note on naming" title="Tritone, augmented 4th, diminished 5th">
        <Prose>
          <p>
            One source of confusion: the same physical interval can have several names depending
            on context. The interval between F and B is six semitones — a <em>tritone</em>. If
            you call it an <strong>augmented 4th</strong>, you are saying it's a 4th that has
            been stretched (used in Lydian-flavoured music). If you call it a
            <strong> diminished 5th</strong>, you are saying it's a 5th that has been squeezed
            (used in dominant 7th chords resolving home). Same sound; different stories about
            where the interval is heading.
          </p>
          <p>
            This is also why we encounter terms like <strong>diminished 7th</strong>,
            <strong> augmented 2nd</strong>, and so on in classical sheet music. The pitches are
            the same as more familiar names; the spellings encode <em>function</em>. This kind
            of context-sensitive naming is what makes notation look complicated and why most pop
            and jazz musicians simplify it to "tritone" and move on.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Cross-cultural" title="Sa Re Re — the Carnatic view">
        <Prose>
          <p>
            Indian classical music, and Carnatic music in particular, classifies intervals
            slightly differently. There are <em>three</em> kinds of "Re" — Re1 (the minor 2nd),
            Re2 (the major 2nd), and Re3 (the minor 3rd / augmented 2nd). The naming is rooted in
            ragas, not in major/minor categories — but the underlying physics is identical to
            the Western system. Music is one species, regardless of which vocabulary you grew up
            with.
          </p>
          <MTQuote>
            All music is based on maths and physics for the most part. And that comes
            together to create emotions which we have no clue about, of course. But the
            backbones of all this is the maths and the physics behind what's going on.
          </MTQuote>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Homework" title="The four-family worksheet">
        <Prose>
          <p>
            Take a single root — A is a good one to start on — and write all thirteen
            intervals from it. Then sort them into the four families: resolutions, anticipations,
            mysteries, and tensions. Play each interval, sing it, and confirm that the family
            tag matches what your ear hears.
          </p>
          <p>
            Repeat on at least three other roots. Once the families are stable in your hearing,
            intervals stop being academic objects and become the literal building blocks of every
            scale, every chord, and every melody you'll write. They are, as Jason puts
            it, <em>the building blocks of everything</em>.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/08a-perfect-4th-and-5th-on-circle.png"
          caption="Jason's note: the circle of fifths shown with directions — clockwise gives Perfect 5ths (P5), counter-clockwise gives Perfect 4ths (P4). One picture, two intervals, twelve keys. This is also why moving 'up a 5th' and moving 'down a 4th' lead to the same pitch."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-9"
        label="Chapter 9 · Circle of Fifths - Climbing using p5 & p4"
        why="Now that intervals are fluent, the circle of fifths becomes a navigation tool — a way to walk between every key on the planet using only perfect fifths and fourths."
      />
    </LessonShell>
  );
}
